import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../context/AuthContext';

/**
 * Props:
 *   redirectTo       – where to send unauthenticated users (default "/login")
 *   requireRole      – a single role string that the user must have
 *   requirePermission– a single permission string the user must have
 *   children         – optional JSX; if provided, children are rendered instead of Outlet
 */
const ProtectedRoute = ({
  redirectTo = '/login',
  requireRole = null,
  requirePermission = null,
  children = null,
}) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  // 1) still checking session?
  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // 2) not logged in → redirect to login, preserve where they were going
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        replace
        state={{ from: location }}
      />
    );
  }

  // 3) role check (if required)
  if (requireRole && user?.role !== requireRole) {
    // you could send them to a "403" page instead
    return <Navigate to="/unauthorized" replace />;
  }

  // 4) permission check (if required)
  if (
    requirePermission &&
    !user?.permissions?.includes(requirePermission)
  ) {
    return <Navigate to="/unauthorized" replace />;
  }

  // 5) finally render either nested routes (Outlet) or the passed children
  return children ? children : <Outlet />;
};

export default ProtectedRoute;
